import { TbCheck } from "react-icons/tb";
import intl from "react-intl-universal";

const CopyButton = ({ value, ariaLabel }) => {
  const handleCopy = async (event) => {
    event?.stopPropagation?.();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!value) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex h-5 shrink-0 items-center rounded border border-gray-200 bg-white px-1.5 font-mono text-[10px] text-gray-600 transition hover:border-[#e7a73f]/40 hover:text-[#9a7340] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300"
      aria-label={ariaLabel}
    >
      {value.length > 12 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value}
    </button>
  );
};

const TrackingStepIcon = ({ status }) => {
  const base = "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold";

  if (status === "completed") {
    return (
      <div className={`${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300`}>
        <TbCheck className="h-3 w-3" />
      </div>
    );
  }

  if (status === "current") {
    return (
      <div
        className={`${base} border-2 border-[#e7a73f] bg-brand-cream text-[#9a7340] dark:bg-brand-dark-elevated dark:text-brand-gold-mid`}
      >
        •
      </div>
    );
  }

  return (
    <div
      className={`${base} border border-gray-200 bg-gray-50 text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-500`}
    />
  );
};

export const CrossborderTrackingTimeline = ({ steps = [], variant = "default" }) => {
  if (!steps.length) return null;

  const isCompact = variant === "compact";

  return (
    <ol className="space-y-0">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const lineColor =
          step.status === "completed"
            ? "bg-emerald-200 dark:bg-emerald-800"
            : "bg-gray-200 dark:bg-brand-dark-border";

        return (
          <li key={step.id ?? index} className={`relative flex gap-2.5 ${isLast ? "" : "pb-3"}`}>
            {!isLast && (
              <span
                className={`absolute left-2.5 top-5 h-[calc(100%-8px)] w-px -translate-x-1/2 ${lineColor}`}
                aria-hidden
              />
            )}

            <TrackingStepIcon status={step.status} />

            <div className="min-w-0 flex-1">
              <p
                className={`text-xs font-semibold leading-tight ${
                  step.status === "pending"
                    ? "text-gray-400 dark:text-gray-500"
                    : "text-brand-dark dark:text-gray-100"
                }`}
              >
                {step.label}
              </p>

              {(step.provider || step.transferId || step.txHash || step.eta || step.note) && (
                <div className="mt-0.5 space-y-0.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400">
                  {step.provider ? (
                    <p>
                      {intl.get("crossborder.detail.provider")}: {step.provider}
                    </p>
                  ) : null}
                  {step.transferId ? (
                    <p className="flex flex-wrap items-center gap-1">
                      {intl.get("crossborder.detail.transferId")}:{" "}
                      <CopyButton
                        value={step.transferId}
                        ariaLabel={intl.get("crossborder.detail.copyTransferId")}
                      />
                    </p>
                  ) : null}
                  {step.txHash ? (
                    <p className="flex flex-wrap items-center gap-1">
                      {intl.get("crossborder.detail.txHash")}:{" "}
                      <CopyButton value={step.txHash} ariaLabel={intl.get("crossborder.detail.copyTxHash")} />
                    </p>
                  ) : null}
                  {step.eta ? (
                    <p>
                      {intl.get("crossborder.detail.eta")}: {step.eta}
                    </p>
                  ) : null}
                  {step.note ? <p className="italic">{step.note}</p> : null}
                </div>
              )}

              {step.completedAt ? (
                <p className={`mt-0.5 text-[10px] text-gray-400 ${isCompact ? "" : ""}`}>{step.completedAt}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
